<template>
<div id="app-container">
  <!-- Public Layout (Not Logged In) -->
  <section v-if="!isLoggedIn" class="public-layout">
    <header>
      <the-main-page></the-main-page>
    </header>
    <main>
      <router-view></router-view>
    </main>
    <footer>
      <the-modern-footer></the-modern-footer>
    </footer>
  </section>

  <!-- Dashboard Layout (Logged In) - With Sidebar -->
  <div v-else class="dashboard-layout">
    <the-sidebar></the-sidebar>
    <div class="dashboard-content" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
      <main class="main-content">
        <router-view></router-view>
      </main>
      <footer class="dashboard-footer">
        <the-modern-footer></the-modern-footer>
      </footer>
    </div>
  </div>

  <!-- Toast Notifications - Always visible -->
  <toast-notification></toast-notification>
</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import TheMainPage from "./views/MainPage.vue";
import TheSidebar from "./components/layout/TheSidebar.vue";
import TheModernFooter from "./components/layout/TheModernFooter.vue";
import ToastNotification from "./components/ui/ToastNotification.vue";
import { useStore } from '@/composables/core/useStore';
import { useRouter } from '@/composables/core/useRouter';

export default defineComponent({
  name: 'App',
  components: {
    TheMainPage,
    TheSidebar,
    TheModernFooter,
    ToastNotification
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    // State: Sidebar collapsed
    const sidebarCollapsed = ref(false);

    // Computed: Check if user is logged in
    const isLoggedIn = computed(() => {
      // Check both correct spelling and token for backward compatibility
      return store.getters['auth/isAuthenticated'] || !!store.getters['auth/token'];
    });

    // Handler: Sidebar toggle event
    const handleSidebarToggle = (event: Event) => {
      const customEvent = event as CustomEvent;
      sidebarCollapsed.value = customEvent.detail.collapsed;
    };

    // Lifecycle: Auto-login on app mount
    onMounted(() => {
      // Root instance initialization - used for reload functionality
      store.dispatch('auth/autoLogin');

      // Listen for sidebar toggle events
      window.addEventListener('sidebar-toggle', handleSidebarToggle);

      // Check initial state from localStorage
      const saved = localStorage.getItem('sidebarCollapsed');
      if (saved !== null) {
        sidebarCollapsed.value = JSON.parse(saved);
      }
    });

    // Lifecycle: Cleanup
    onBeforeUnmount(() => {
      window.removeEventListener('sidebar-toggle', handleSidebarToggle);
    });

    // Watch: Redirect to auth page when user logs out
    watch(isLoggedIn, (curValue, oldValue) => {
      if (!curValue && oldValue === true && curValue !== oldValue) {
        console.log("[App] User logged out, redirecting to /auth");

        // Try Vue Router first
        router.replace('/auth').catch(() => {
          // Fallback to window.location if router fails
          console.log("[App] Router redirect failed, using window.location");
          window.location.href = '/auth';
        });
      }
    });

    return {
      isLoggedIn,
      sidebarCollapsed
    };
  }
});
</script>

<style>
/* ============================================
   Global Styles
   ============================================ */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

#app {
  font-family: 'Avenir', 'Segoe UI', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  height: 100%;
}

html {
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
  height: 100%;
  width: 100%;
  scroll-behavior: smooth;
}

body {
  min-height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
}

#app-container {
  height: 100%;
  width: 100%;
  min-height: 100vh;
}

h1 {
  width: 100%;
  color: #2c3e50;
  font-weight: 700;
}

/* ============================================
   Public Layout (Not Logged In)
   ============================================ */
.public-layout {
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    "header"
    "main"
    "footer";
  min-height: 100vh;
}

.public-layout header {
  grid-area: header;
}

.public-layout main {
  grid-area: main;
  padding: 1.5rem;
  width: 100%;
  margin: 0 auto;
  padding-bottom: 2rem;
}

.public-layout footer {
  grid-area: footer;
  margin-top: auto;
}

/* ============================================
   Dashboard Layout (Logged In) - With Sidebar
   ============================================ */
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
}

.dashboard-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 230px;
  width: calc(100% - 230px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dashboard-content.sidebar-collapsed {
  margin-left: 80px;
  width: calc(100% - 80px);
}

.main-content {
  flex: 1 0 auto; /* Grow to fill space, don't shrink, auto basis */
  padding: 0 2rem;
  width: 100%;
  margin: 0 auto;
  animation: slideInContent 0.4s ease-out;
}

.dashboard-footer {
  margin-top: auto; /* Push footer to bottom when content is short */
  padding-top: 2rem; /* Spacing above footer */
  flex-shrink: 0; /* Never shrink the footer */
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(25, 135, 84, 0.1);
}

/* ============================================
   Responsive Design - Mobile
   ============================================ */
@media (max-width: 968px) {
  .dashboard-content {
    margin-left: 0;
  }

  .main-content {
    padding: 5rem 0.5rem 0; /* Top space for mobile toggle, minimal horizontal padding */
  }
}

@media (max-width: 768px) {
  .public-layout main {
    padding: 1rem;
  }

  .main-content {
    padding: 4.5rem 0.25rem 0;
  }
}

@media (max-width: 480px) {
  .public-layout main {
    padding: 0.75rem;
  }

  .main-content {
    padding: 4.5rem 0 0; /* No horizontal padding on very small screens */
  }
}

/* ============================================
   Animations
   ============================================ */
@keyframes slideInContent {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>